import { useEffect, useMemo, useState, type DragEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { useSearchParams } from "react-router";
import {
  SaveCollectionControls,
  SaveCollectionEmptyState,
  SaveCollectionGrid,
  SaveCollectionPage,
  SaveCollectionSidebar,
  SaveGroupDeleteConfirm,
  SaveGroupNameDialog,
  SaveGroupPopover,
  SavePersonCollectionCard,
  SaveRemoveItemDialog,
  type SaveGroupLite,
} from "@/components/save/SaveCollectionTemplate";
import { useMe } from "@/auth/use-me";
import { applySearchSort, type SortDir } from "@/lib/collection-filter";
import {
  CreatePersonGroupDocument,
  DeletePersonGroupDocument,
  MyContactsDocument,
  MyPersonGroupsDocument,
  PersonGroupMembershipsDocument,
  UpdatePersonGroupDocument,
  type MyContactsQuery,
  type PersonFilterInput,
} from "@/graphql/generated/operations";
import { useContact } from "@/pages/save/use-contact";
import { LoadMore } from "@/components/common/LoadMore";
import { usePagedQuery } from "@/lib/use-paged-query";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { throwForValidationResult, userFacingGraphqlError } from "@/lib/graphql-error";

// Save · Community. Master-detail layout like the Pins page (PinboardPage):
// a left list (Alle / Ohne Gruppe / the communities) selects what the right
// grid shows. A single contacts query is re-keyed by the selection (nodes don't
// carry their group membership, so client-side grouping isn't possible).
// Contacts move into a community via the per-card "Verschieben" select or by
// dragging a card onto a community in the list.

type GroupSelection = "all" | string;

type GroupLite = SaveGroupLite;

interface Contact {
  id: string;
  formattedName?: string | null;
  name?: { given?: (string | null)[] | null; family?: string | null } | null;
  profession?: string | null;
  city?: string | null;
  country?: { name?: string | null } | null;
  avatar?: { mimeType?: string | null; content?: string | null } | null;
}

function fullName(c: Contact): string {
  if (c.formattedName) return c.formattedName;
  const given = (c.name?.given ?? []).filter(Boolean).join(" ");
  return [given, c.name?.family].filter(Boolean).join(" ") || "–";
}

function subtitleOf(c: Contact): string {
  return [c.profession, c.city, c.country?.name].filter(Boolean).join(" · ");
}

function whereFor(sel: GroupSelection, onlyUngrouped: boolean): PersonFilterInput {
  if (sel !== "all") {
    // A community's members.
    return { personGroupId: { eq: sel } };
  }
  if (onlyUngrouped) {
    // genesis applies only ONE top-level filter field (PersonFilterSpec early-
    // returns on the first match), so isContact + isInAnyPersonGroup must be
    // combined via `and` to AND both - otherwise isContact is dropped and the
    // list leaks non-contacts. `and` INNER JOINs both sub-filters (FilterSpec).
    return { and: [{ isContact: { eq: true } }, { isInAnyPersonGroup: { eq: false } }] };
  }
  // "Alle": every contact (grouped contacts carry isContact too). "Nur ohne
  // Gruppe" is a strict subset of this.
  return { isContact: { eq: true } };
}

export function CommunityManager() {
  const intl = useIntl();
  const me = useMe();
  const client = useApolloClient();
  // Deep link from the home "+ Community" quick action: open the create dialog
  // on mount (captured once via the initializer), then strip the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeGroup, setActiveGroup] = useState<GroupSelection>("all");
  const [onlyUngrouped, setOnlyUngrouped] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortDir>("az");
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "rename"; group: GroupLite } | null
  >(() => (searchParams.get("create") === "group" ? { mode: "create" } : null));
  useEffect(() => {
    if (searchParams.has("create")) {
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [deleting, setDeleting] = useState<GroupLite | null>(null);
  const [removingContact, setRemovingContact] = useState<Contact | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [dialogIssue, setDialogIssue] = useState<string | null>(null);

  const groupsQ = useQuery(MyPersonGroupsDocument, { variables: { input: {} } });
  const contactsInput = useMemo(
    () => ({ where: whereFor(activeGroup, onlyUngrouped) }),
    [activeGroup, onlyUngrouped],
  );
  const contactsPaged = usePagedQuery<MyContactsQuery["persons"]["nodes"][number]>(
    MyContactsDocument,
    { connectionKey: "persons", input: contactsInput, pageSize: 30 },
  );

  const [createGroup, { loading: creating }] = useMutation(CreatePersonGroupDocument);
  const [updateGroup, { loading: updating }] = useMutation(UpdatePersonGroupDocument);
  const [deleteGroup] = useMutation(DeletePersonGroupDocument);
  const contact = useContact();

  // Refetch by operation name so every active query of that name updates at once
  // (group list, the contact grid, and the total count). useContact handles
  // PersonGroupMemberships itself; this covers the page-specific lists.
  const refetchPageLists = { include: ["MyPersonGroups", "MyContacts"] };

  // personGroups is a single-member union (PersonGroupResult); unwrap via the
  // any seam, same as ServicePage does for documentGroups.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupsOut: any = groupsQ.data?.personGroups;
  const groups = ((groupsOut?.personGroups ?? []) as readonly {
    id: string;
    name: string;
    itemCount?: number | null;
  }[]).map((g) => ({ id: g.id, name: g.name, itemCount: g.itemCount ?? 0 }));

  const contacts = applySearchSort(
    contactsPaged.nodes as Contact[],
    (c) => fullName(c),
    search,
    sort,
  );

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): counts + view-state only, never the contact
  // records themselves (other people's PII — the assistant fetches those via tools if needed).
  const activeGroupName =
    activeGroup === "all" ? null : groups.find((g) => g.id === activeGroup)?.name ?? null;
  // Guided tour (/tour): summary + the sidebar, contact grid and controls, with live counts.
  useAssistantTour(
    contactsPaged.loading && !contactsPaged.nodes.length
      ? null
      : {
          label: intl.formatMessage({
            id: "community.tour.label",
            defaultMessage: "Save Community",
          }),
          summary: intl.formatMessage(
            {
              id: "community.tour.summary",
              defaultMessage:
                "This is your Community page: {contacts} contacts, organized into {groups} communities.",
            },
            { contacts: contacts.length, groups: groups.length },
          ),
          stops: [
            {
              anchor: "save.community.groups",
              title: intl.formatMessage({ id: "community.tour.groups.title", defaultMessage: "Your communities" }),
              description: intl.formatMessage(
                {
                  id: "community.tour.groups.desc",
                  defaultMessage:
                    "{count} communities to group your contacts. Click one to see its members, create new ones via \"+ Community\", and drag a contact card onto a community to add it.",
                },
                { count: groups.length },
              ),
            },
            {
              anchor: "save.community.contacts",
              title: intl.formatMessage({ id: "community.tour.contacts.title", defaultMessage: "Your contacts" }),
              description: intl.formatMessage(
                {
                  id: "community.tour.contacts.desc",
                  defaultMessage:
                    "{count} contacts in the current view. Click a card to open the profile, use \"+ Add\" to put it into communities, or drag it onto one on the left.",
                },
                { count: contacts.length },
              ),
            },
            {
              anchor: "save.community.controls",
              title: intl.formatMessage({ id: "community.tour.controls.title", defaultMessage: "Search & sort" }),
              description: intl.formatMessage({
                id: "community.tour.controls.desc",
                defaultMessage:
                  "Search contacts by name, change the sort order, or filter to contacts that are not in any community yet.",
              }),
            },
          ],
        },
  );

  useAssistantContext(
    contactsPaged.loading && !contactsPaged.nodes.length
      ? null
      : [
          {
            description: "the user's Community (contacts) page",
            value:
              `${contacts.length} contacts shown, ${groups.length} communities` +
              (activeGroupName ? `; viewing community "${activeGroupName}"` : "") +
              (onlyUngrouped ? "; filtered to ungrouped" : "") +
              (search ? `; search "${search}"` : ""),
          },
        ],
  );

  async function handleSaveGroup(name: string, id?: string) {
    setErrorMsg(null);
    setDialogIssue(null);
    try {
      if (id) {
        const res = await updateGroup({
          variables: { id, input: { data: { name }, dryRun: false } },
          refetchQueries: ["MyPersonGroups"],
          awaitRefetchQueries: true,
        });
        const out = res.data?.updatePersonGroup;
        if (out?.__typename !== "UpdatePersonGroupResult") {
          throwForValidationResult(
            out,
            intl.formatMessage({
              id: "save.community.renameError",
              defaultMessage: "Community could not be renamed.",
            }),
          );
        }
      } else {
        const res = await createGroup({
          variables: { input: { person: me.id ?? undefined, data: { name }, dryRun: false } },
          refetchQueries: ["MyPersonGroups"],
          awaitRefetchQueries: true,
        });
        const out = res.data?.createPersonGroup;
        if (out?.__typename === "TooManyRecordsResult") {
          setErrorMsg(
            intl.formatMessage(
              {
                id: "save.community.limitReached",
                defaultMessage: "You have reached the maximum of {limit} communities.",
              },
              { limit: out.limit ?? "" },
            ),
          );
          setDialog(null);
          return;
        }
        if (out?.__typename !== "CreatePersonGroupResult") {
          throwForValidationResult(
            out,
            intl.formatMessage({
              id: "save.community.createError",
              defaultMessage: "Community could not be created.",
            }),
          );
        }
      }
      setDialog(null);
    } catch (e) {
      // Inside the dialog, not behind it: the modal stays open on failure, so a page-level message is
      // rendered under a blurred backdrop the user cannot read.
      setDialogIssue(
        userFacingGraphqlError(
          e,
          intl.formatMessage({
            id: "save.community.saveError",
            defaultMessage: "Community could not be saved.",
          }),
        ),
      );
    }
  }

  // Keep-only: genesis cascades the membership rows but keeps the contacts, so
  // a deleted community's members reappear under "Ohne Gruppe".
  async function confirmDelete() {
    if (!deleting) return;
    const id = deleting.id;
    setDeleting(null);
    setErrorMsg(null);
    try {
      await deleteGroup({
        variables: { input: { id, dryRun: false } },
        refetchQueries: ["MyPersonGroups", "MyContacts", "PersonGroupMemberships"],
        awaitRefetchQueries: true,
      });
      if (activeGroup === id) setActiveGroup("all");
    } catch (e) {
      setErrorMsg(
        userFacingGraphqlError(
          e,
          intl.formatMessage({
            id: "save.community.deleteError",
            defaultMessage: "Community could not be deleted.",
          }),
        ),
      );
    }
  }

  // Add a contact to a community. Additive (removeContactsFromOtherGroups:
  // false) - a contact can belong to several communities at once.
  async function addToGroup(contactId: string, groupId: string) {
    if (!groupId) return;
    setErrorMsg(null);
    try {
      await contact.add({
        contactIds: [contactId],
        groups: [groupId],
        removeContactsFromOtherGroups: false,
      });
      await client.refetchQueries(refetchPageLists);
    } catch (e) {
      const isQuota = e instanceof Error && e.message === "DailyQuotaExceeded";
      setErrorMsg(
        isQuota
          ? intl.formatMessage({
              id: "save.community.dailyQuotaExceeded",
              defaultMessage: "Daily limit reached. Please try again later.",
            })
          : userFacingGraphqlError(
              e,
              intl.formatMessage({
                id: "save.community.moveFailed",
                defaultMessage: "Move failed.",
              }),
            ),
      );
    }
  }

  async function removeContact(id: string) {
    setErrorMsg(null);
    try {
      await contact.remove({ contactIds: [id] });
      await client.refetchQueries(refetchPageLists);
    } catch (e) {
      setErrorMsg(
        userFacingGraphqlError(
          e,
          intl.formatMessage({
            id: "save.community.removeContactError",
            defaultMessage: "Contact could not be removed.",
          }),
        ),
      );
    }
  }

  // Remove a contact from ONE community while keeping the contact and any other
  // memberships: read current memberships, drop the target group, and re-set the
  // remaining set (removeContactsFromOtherGroups deletes exactly the dropped one).
  async function removeFromCommunity(contactId: string, groupId: string) {
    setErrorMsg(null);
    try {
      const res = await client.query({
        query: PersonGroupMembershipsDocument,
        variables: { input: { personId: contactId } },
        fetchPolicy: "network-only",
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const out: any = res.data?.personGroupMemberships;
      const current = ((out?.personGroups ?? []) as { id: string }[]).map((g) => g.id);
      const remaining = current.filter((gid) => gid !== groupId);
      await contact.add({
        contactIds: [contactId],
        groups: remaining,
        removeContactsFromOtherGroups: true,
      });
      await client.refetchQueries(refetchPageLists);
    } catch (e) {
      setErrorMsg(
        userFacingGraphqlError(
          e,
          intl.formatMessage({
            id: "save.community.removeFromCommunityFailed",
            defaultMessage: "Removing from the community failed.",
          }),
        ),
      );
    }
  }

  // A community list row is a drop target: dropping a dragged contact moves it
  // into that community.
  const dropHandlers = (groupId: string) => ({
    onDragOver: (e: DragEvent) => {
      if (!draggingId) return;
      // Dropping on "Alle" only does something from inside a community (it
      // removes that membership); no source group → no drop affordance.
      if (groupId === "all" && activeGroup === "all") return;
      e.preventDefault();
      if (dropTarget !== groupId) setDropTarget(groupId);
    },
    onDragLeave: () => setDropTarget((t) => (t === groupId ? null : t)),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      if (draggingId) {
        if (groupId === "all") {
          // Drag from a community to "Alle" → remove it from the active community.
          if (activeGroup !== "all") void removeFromCommunity(draggingId, activeGroup);
        } else {
          void addToGroup(draggingId, groupId);
        }
      }
      setDropTarget(null);
      setDraggingId(null);
    },
  });

  const headerCount = intl.formatMessage(
    {
      id: "save.community.headerCount",
      defaultMessage: "{count, plural, one {# contact} other {# contacts}}",
    },
    { count: contacts.length },
  );

  // When a single community is open, the trash offers "remove from this
  // community" in addition to "delete contact entirely".
  const activeCommunity =
    activeGroup !== "all" ? (groups.find((g) => g.id === activeGroup) ?? null) : null;

  return (
    <>
      <SaveCollectionPage
        title={intl.formatMessage({ id: "save.community.title", defaultMessage: "Community" })}
        dataGraphql="MyPersonGroups,MyContacts"
        showBackButton
        description={
          <FormattedMessage
            id="save.community.description"
            defaultMessage='Group your contacts. Select a community on the left and drag contacts into it or add them to one or more communities via "Add".'
          />
        }
        sidebar={
          <div data-assistant-anchor="save.community.groups">
          <SaveCollectionSidebar
            topControls={
              // Invisible spacer mirroring the SegmentedControl on the Pins page
              // (Personen / Posts / Projekte) so the "Alle" tile and the right-
              // hand controls row line up identically across both pages.
              <div className="smycles-pill-group invisible w-full" aria-hidden>
                <div className="smycles-pill flex-1 px-4 py-1.5 text-[11px]">&nbsp;</div>
              </div>
            }
            groups={groups}
            activeGroup={activeGroup}
            addLabel={intl.formatMessage({
              id: "save.community.addCommunity",
              defaultMessage: "+ Community",
            })}
            renameLabel={intl.formatMessage({
              id: "save.community.renameCommunity",
              defaultMessage: "Rename community",
            })}
            deleteLabel={intl.formatMessage({
              id: "save.community.deleteCommunity",
              defaultMessage: "Delete community",
            })}
            onGroupChange={setActiveGroup}
            onCreateGroup={() => setDialog({ mode: "create" })}
            onRenameGroup={(group) => setDialog({ mode: "rename", group })}
            onDeleteGroup={setDeleting}
            dropTargetId={dropTarget}
            dropHandlers={dropHandlers}
          />
          </div>
        }
      >
        <div data-assistant-anchor="save.community.controls">
        <SaveCollectionControls
          statusLabel={headerCount}
          loading={contactsPaged.loading}
          errorMessage={contactsPaged.error ? userErrorMessage(intl, contactsPaged.error) : undefined}
          search={search}
          sort={sort}
          showUngroupedToggle={activeGroup === "all"}
          ungroupedLabel={intl.formatMessage({
            id: "save.community.ungroupedLabel",
            defaultMessage: "Contacts without a community",
          })}
          ungroupedActive={onlyUngrouped}
          onSearchChange={setSearch}
          onSortChange={setSort}
          onUngroupedToggle={() => setOnlyUngrouped((v) => !v)}
        />
        </div>

        <div data-assistant-anchor="save.community.contacts">
        {!contactsPaged.loading && !contactsPaged.error && contacts.length === 0 ? (
          <SaveCollectionEmptyState>
            {activeGroup === "all" ? (
              onlyUngrouped ? (
                <FormattedMessage
                  id="save.community.emptyAllGrouped"
                  defaultMessage="All contacts are in communities."
                />
              ) : (
                <FormattedMessage
                  id="save.community.emptyNoContacts"
                  defaultMessage="No contacts yet."
                />
              )
            ) : (
              <FormattedMessage
                id="save.community.emptyCommunity"
                defaultMessage='No one in this community yet. Drag contacts here or use "Add".'
              />
            )}
          </SaveCollectionEmptyState>
        ) : (
          <SaveCollectionGrid>
            {contacts.map((c) => (
              <ContactCard
                key={c.id}
                contact={c}
                onRemove={() => setRemovingContact(c)}
                groups={groups}
                onAdd={(gid) => addToGroup(c.id, gid)}
                onRemoveGroup={(gid) => removeFromCommunity(c.id, gid)}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", c.id);
                  setDraggingId(c.id);
                }}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDropTarget(null);
                }}
                />
              ))}
          </SaveCollectionGrid>
        )}
        </div>

        <LoadMore
          hasMore={contactsPaged.hasMore}
          loading={contactsPaged.loadingMore}
          paused={contactsPaged.loadMoreFailed}
          onLoadMore={contactsPaged.loadMore}
        />
      </SaveCollectionPage>

      {errorMsg ? <div className="mt-3 text-[11px] text-red-600">{errorMsg}</div> : null}

      {dialog !== null ? (
        <SaveGroupNameDialog
          open
          title={
            dialog.mode === "rename"
              ? intl.formatMessage({
                  id: "save.community.renameCommunity",
                  defaultMessage: "Rename community",
                })
              : intl.formatMessage({
                  id: "save.community.newCommunity",
                  defaultMessage: "New community",
                })
          }
          initial={dialog.mode === "rename" ? dialog.group.name : ""}
          existingNames={groups.map((g) => g.name)}
          placeholder={intl.formatMessage({
            id: "save.community.namePlaceholder",
            defaultMessage: "Community name …",
          })}
          saving={creating || updating}
          onClose={() => {
            setDialogIssue(null);
            setDialog(null);
          }}
          issue={dialogIssue}
          onSave={(name) =>
            void handleSaveGroup(name, dialog.mode === "rename" ? dialog.group.id : undefined)
          }
        />
      ) : null}

      <SaveGroupDeleteConfirm
        open={deleting !== null}
        title={intl.formatMessage({
          id: "save.community.deleteCommunity",
          defaultMessage: "Delete community",
        })}
        message={
          deleting
            ? intl.formatMessage(
                {
                  id: "save.community.deleteConfirm",
                  defaultMessage:
                    'Delete "{name}"? The contacts are kept, only the grouping is removed.',
                },
                { name: deleting.name },
              )
            : undefined
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />

      {removingContact ? (
        <SaveRemoveItemDialog
          open
          title={intl.formatMessage({
            id: "save.community.removeContact",
            defaultMessage: "Remove contact",
          })}
          onClose={() => setRemovingContact(null)}
          message={
            activeCommunity ? (
              <FormattedMessage
                id="save.community.removeFromCommunityPrompt"
                defaultMessage='Remove "{name}" from "{community}" (stays your contact and in other communities) or delete completely from your contacts and all communities?'
                values={{ name: fullName(removingContact), community: activeCommunity.name }}
              />
            ) : (
              <FormattedMessage
                id="save.community.deleteContactPrompt"
                defaultMessage='Delete "{name}" from your contacts and all communities?'
                values={{ name: fullName(removingContact) }}
              />
            )
          }
          secondaryActionLabel={
            activeCommunity
              ? intl.formatMessage({
                  id: "save.community.removeFromGroup",
                  defaultMessage: "Remove from group",
                })
              : undefined
          }
          onSecondaryAction={
            activeCommunity
              ? () => {
                  const c = removingContact;
                  setRemovingContact(null);
                  if (c) void removeFromCommunity(c.id, activeCommunity.id);
                }
              : undefined
          }
          destructiveActionLabel={intl.formatMessage({
            id: "save.community.deleteCompletely",
            defaultMessage: "Delete completely",
          })}
          onDestructiveAction={() => {
            const c = removingContact;
            setRemovingContact(null);
            if (c) void removeContact(c.id);
          }}
        />
      ) : null}
    </>
  );
}

// "+ Hinzufügen" pill: opens a popover of communities; clicking a community
// toggles membership for this contact (add if not a member, remove if already
// one). Members are marked with ✓; membership is loaded lazily when the popover
// opens and updates live after a toggle. The popover stays open so several can
// be toggled in one go.
function AddToCommunity({
  contactId,
  options,
  onAdd,
  onRemoveGroup,
}: {
  contactId: string;
  options: GroupLite[];
  onAdd: (groupId: string) => Promise<void> | void;
  onRemoveGroup: (groupId: string) => Promise<void> | void;
}) {
  const intl = useIntl();
  // Group id whose add/remove is currently in flight, so we can show a spinner
  // on exactly that pill until genesis responds.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { data } = useQuery(PersonGroupMembershipsDocument, {
    variables: { input: { personId: contactId } },
    fetchPolicy: "cache-and-network",
  });
  // personGroupMemberships is a single-member union; unwrap via the any seam.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const memberOut: any = data?.personGroupMemberships;
  const memberIds = new Set<string>(
    ((memberOut?.personGroups ?? []) as { id: string }[]).map((g) => g.id),
  );

  async function toggle(groupId: string, isMember: boolean) {
    if (pendingId) return;
    setPendingId(groupId);
    try {
      await (isMember ? onRemoveGroup(groupId) : onAdd(groupId));
    } finally {
      setPendingId(null);
    }
  }

  if (options.length === 0) return null;
  return (
    <SaveGroupPopover
      triggerLabel={intl.formatMessage({
        id: "save.community.addTrigger",
        defaultMessage: "+ Add",
      })}
      options={options}
      memberIds={memberIds}
      pendingId={pendingId}
      onToggle={(groupId, isMember) => void toggle(groupId, isMember)}
    />
  );
}

function ContactCard({
  contact,
  onRemove,
  groups,
  onAdd,
  onRemoveGroup,
  draggable,
  onDragStart,
  onDragEnd,
}: {
  contact: Contact;
  onRemove: () => void;
  groups: GroupLite[];
  onAdd: (groupId: string) => Promise<void> | void;
  onRemoveGroup: (groupId: string) => Promise<void> | void;
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
}) {
  const intl = useIntl();
  const name = fullName(contact);
  const subtitle = subtitleOf(contact);
  return (
    <SavePersonCollectionCard
      id={contact.id}
      title={name}
      subtitle={subtitle}
      avatarContent={contact.avatar?.content}
      avatarMime={contact.avatar?.mimeType}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      openHref={`/p/${contact.id}`}
      onRemove={onRemove}
      removeLabel={intl.formatMessage({
        id: "save.community.removeContact",
        defaultMessage: "Remove contact",
      })}
      actions={
        <AddToCommunity
          contactId={contact.id}
          options={groups}
          onAdd={onAdd}
          onRemoveGroup={onRemoveGroup}
        />
      }
    />
  );
}
